import React, { useState } from 'react';
import ScheduleCallModal from './ScheduleCallModal';
import ChatWithSalesModal from './ChatWithSalesModal';

interface PricingPageProps {
  onBack: () => void;
}

const PricingPage: React.FC<PricingPageProps> = ({ onBack }) => {
  const [showScheduleCall, setShowScheduleCall] = useState(false);
  const [showChatSales, setShowChatSales] = useState(false);
  const plans = [
    {
      name: 'Starter',
      description: 'Perfect for small schools, preschools & coaching centres starting their digital journey.',
      color: '#28A745',
      icon: 'bi-rocket-takeoff',
      features: [
        'Student Information System (SIS)',
        'Attendance & Timetable',
        'Basic Fee Management',
        'Homework & Announcements',
        'Parent App (Standard)',
        'Up to 300 students',
        'Email Support'
      ],
      ctaText: 'Get Started',
      ctaVariant: 'outline'
    },
    {
      name: 'Growth',
      description: 'Best for growing schools that want automation, insights & higher efficiency.',
      color: '#5C2597',
      icon: 'bi-graph-up-arrow',
      badge: 'Most Popular',
      features: [
        'Everything in Starter, plus:',
        'AI-Powered Insights & Smart Reports',
        'Automated Fee Reminders',
        'Exam Management + Report Cards',
        'HR + Payroll',
        'Transport Management',
        'Advanced Parent App',
        'Up to 1,000 students',
        'Priority Support'
      ],
      ctaText: 'Upgrade to Growth',
      ctaVariant: 'solid',
      highlighted: true
    },
    {
      name: 'Enterprise',
      description: 'Ideal for large institutions & school groups requiring complete customisation.',
      color: '#2D7DD2',
      icon: 'bi-building',
      features: [
        'Everything in Growth, plus:',
        'Multi-Branch Management',
        'Custom Workflows & Branding',
        'API Integrations (LMS, ERP, Biometrics)',
        'Advanced AI Modules',
        'Unlimited Students',
        'Dedicated Account Manager',
        '24×7 Support'
      ],
      ctaText: 'Request a Demo',
      ctaVariant: 'outline'
    }
  ];

  return (
    <div className="min-vh-100 bg-light">
      {/* Header */}
      <div className="bg-primary-custom text-white shadow-sm">
        <div className="container-fluid px-4 py-3">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">
              <i className="bi bi-tag-fill me-2"></i>
              Pricing Plans
            </h2>
            <button className="btn btn-light btn-sm" onClick={onBack}>
              <i className="bi bi-arrow-left me-2"></i>Back
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="container-fluid px-4 py-5">
        <div className="text-center mb-5">
          <h1 className="display-4 fw-bold text-primary-custom mb-3">
            Choose the Right Plan for Your Institution
          </h1>
          <p className="lead text-muted">
            Flexible plans designed to scale with your institution's needs.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="row g-4 justify-content-center mb-5">
          {plans.map((plan, index) => (
            <div key={index} className="col-12 col-lg-4">
              <div
                className={`card h-100 border-0 shadow-sm ${
                  plan.highlighted ? 'border-3 border-primary-custom' : ''
                }`}
                style={{
                  transform: plan.highlighted ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.3s ease'
                }}
              >
                {plan.badge && (
                  <div className="position-absolute top-0 start-50 translate-middle">
                    <span className="badge bg-primary-custom text-white px-3 py-2">
                      <i className="bi bi-star-fill me-1"></i>
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="card-body p-4">
                  {/* Plan Header */}
                  <div className="text-center mb-4">
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: '80px',
                        height: '80px',
                        backgroundColor: `${plan.color}20`,
                        color: plan.color
                      }}
                    >
                      <i className={`${plan.icon} fs-1`}></i>
                    </div>
                    <h3 className="fw-bold mb-3">{plan.name}</h3>
                    <p className="text-muted small">{plan.description}</p>
                  </div>

                  {/* Features List */}
                  <div className="mb-4">
                    <h6 className="fw-bold mb-3">Includes:</h6>
                    <ul className="list-unstyled">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="mb-2">
                          <i
                            className="bi bi-check-circle-fill me-2"
                            style={{ color: plan.color }}
                          ></i>
                          <span className="text-secondary">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <div className="d-grid">
                    <button
                      className={`btn ${
                        plan.ctaVariant === 'solid'
                          ? 'btn-primary-custom text-white'
                          : 'btn-outline-primary'
                      } btn-lg`}
                      style={
                        plan.ctaVariant === 'outline'
                          ? { borderColor: plan.color, color: plan.color }
                          : {}
                      }
                    >
                      {plan.ctaText}
                      <i className="bi bi-arrow-right ms-2"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Information */}
        <div className="row g-4 mt-5">
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center p-4">
                <i className="bi bi-shield-check text-success fs-1 mb-3"></i>
                <h5 className="fw-bold mb-2">Secure & Compliant</h5>
                <p className="text-muted small mb-0">
                  Your data is encrypted and stored securely with regular backups
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center p-4">
                <i className="bi bi-headset text-primary fs-1 mb-3"></i>
                <h5 className="fw-bold mb-2">24/7 Support</h5>
                <p className="text-muted small mb-0">
                  Our support team is always ready to help you succeed
                </p>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body text-center p-4">
                <i className="bi bi-arrow-repeat text-warning fs-1 mb-3"></i>
                <h5 className="fw-bold mb-2">Free Updates</h5>
                <p className="text-muted small mb-0">
                  Get all new features and improvements at no extra cost
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="row mt-5">
          <div className="col-lg-8 mx-auto">
            <h3 className="text-center fw-bold mb-4">Frequently Asked Questions</h3>
            <div className="accordion" id="pricingFAQ">
              <div className="accordion-item border-0 shadow-sm mb-3">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#faq1"
                  >
                    Can I upgrade my plan later?
                  </button>
                </h2>
                <div id="faq1" className="accordion-collapse collapse show" data-bs-parent="#pricingFAQ">
                  <div className="accordion-body">
                    Yes! You can upgrade your plan at any time. The pricing difference will be prorated based on your remaining subscription period.
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 shadow-sm mb-3">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#faq2"
                  >
                    What happens if I exceed the student limit?
                  </button>
                </h2>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#pricingFAQ">
                  <div className="accordion-body">
                    We'll notify you when you're approaching your limit. You can upgrade to the next plan to accommodate more students seamlessly.
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 shadow-sm mb-3">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#faq3"
                  >
                    Is there a free trial available?
                  </button>
                </h2>
                <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#pricingFAQ">
                  <div className="accordion-body">
                    Yes! We offer a 14-day free trial with full access to all features in your chosen plan. No credit card required.
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 shadow-sm">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed fw-semibold"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#faq4"
                  >
                    What payment methods do you accept?
                  </button>
                </h2>
                <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#pricingFAQ">
                  <div className="accordion-body">
                    We accept all major credit cards, debit cards, net banking, UPI, and bank transfers for annual subscriptions.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="row mt-5">
          <div className="col-lg-8 mx-auto">
            <div className="card border-0 shadow-sm bg-primary-custom text-white">
              <div className="card-body text-center p-5">
                <h3 className="fw-bold mb-3">Still have questions?</h3>
                <p className="mb-4">
                  Our team is here to help you choose the right plan for your institution.
                </p>
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <button className="btn btn-light btn-lg" onClick={() => setShowScheduleCall(true)}>
                    <i className="bi bi-calendar-check me-2"></i>
                    Schedule a Call
                  </button>
                  <button className="btn btn-outline-light btn-lg" onClick={() => setShowChatSales(true)}>
                    <i className="bi bi-chat-dots me-2"></i>
                    Chat with Sales
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <ScheduleCallModal show={showScheduleCall} onClose={() => setShowScheduleCall(false)} />
      <ChatWithSalesModal show={showChatSales} onClose={() => setShowChatSales(false)} />
    </div>
  );
};

export default PricingPage;
